import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

/** Что сервер сказал про одну переписку: заглушена она или нет. */
export type ChatMuteState = { chatId: string; muted: boolean };

type ChatSettingsSlice = {
  soundEnabled: boolean;
  toggleSound: () => void;
  /*
    Заглушённые переписки. С 04.09.2026 хранит их сервер (`chat_members.muted`), и здесь лежит
    его зеркало: всплывающему уведомлению ответ нужен мгновенно, а лезть за ним в кеш запросов из
    обработчика сокета неоткуда. Пишется в четырёх местах — один раз после входа списком на весь
    аккаунт (`replaceMutedChats`), при загрузке списка чатов, при открытии самой переписки
    (`syncMutedChats`) и сразу после переключателя, чтобы экран не ждал ответа.
  */
  mutedChatIds: string[];
  /**
   * Переписать зеркало целиком по списку на весь аккаунт.
   *
   * Зовётся один раз после входа: сервер отдаёт все заглушённые переписки сразу, поэтому здесь
   * замена, а не слияние - иначе на новом устройстве зеркало осталось бы пустым до того, как
   * человек сам зайдёт на экран чатов, и первая всплывашка из заглушённой переписки проскочила бы.
   * Ровно один запрос за сессию (по фокусу и переподключению не перечитывается), чтобы он не
   * затирал переключатель, нажатый секунду назад.
   */
  replaceMutedChats: (all: string[]) => void;
  /**
   * Разложить ответ сервера про те переписки, о которых он рассказал.
   *
   * 🔴 Раньше подменял весь список целиком, и зеркало умело помнить только первую страницу
   * вкладки «Все»: заглушённая переписка ниже двадцатой пропадала из него на первом же заходе на
   * экран чатов, после чего всплывашка о ней снова выскакивала, а значок в шапке предлагал
   * «Заглушить» уже заглушённой. Обновляем поимённо: про что сервер сказал — то и пишем,
   * остальное не трогаем.
   */
  syncMutedChats: (known: ChatMuteState[]) => void;
  toggleChatMute: (chatId: string) => void;
  /*
    Текст, который сервер продолжает показывать в превью списка после удаления сообщения (MSG-2).
    Сервер снимает текст у самого сообщения, но поле «последнее сообщение» переписки не трогает,
    и список чатов показывал удалённое слово в слово. Читатель помнит, что именно удалил, и пока
    сервер отдаёт тот же текст, показывает «Сообщение удалено». Новая реплика меняет текст, и
    запись перестаёт совпадать сама.
  */
  deletedTails: Record<string, string>;
  noteDeletedTail: (chatId: string, text: string) => void;
  isChatMuted: (chatId: string | null | undefined) => boolean;
};

/** Сколько переписок помним: хватает на недавние, а хранилище не растёт без границы. */
const MAX_DELETED_TAILS = 50;

export const useChatSettingsStore = create<ChatSettingsSlice>()(
  persist(
    (set, get) => ({
      soundEnabled: true,
      toggleSound: () => {
        set((state) => ({ soundEnabled: !state.soundEnabled }));
      },
      mutedChatIds: [],
      replaceMutedChats: (all) => {
        set((state) => {
          const next = all.filter(Boolean);
          // Прежний массив, когда состав тот же: новая ссылка перерисовала бы всех подписчиков
          // на каждом заходе в приложение.
          if (
            next.length === state.mutedChatIds.length &&
            state.mutedChatIds.every((id) => next.includes(id))
          ) {
            return state;
          }
          return { mutedChatIds: next };
        });
      },
      syncMutedChats: (known) => {
        set((state) => {
          const next = new Set(state.mutedChatIds);
          for (const { chatId, muted } of known) {
            if (!chatId) {
              continue;
            }
            if (muted) {
              next.add(chatId);
            } else {
              next.delete(chatId);
            }
          }
          // Возвращаем прежний массив, когда ничего не изменилось: новая ссылка перерисовала бы
          // всех подписчиков на каждой загрузке списка чатов.
          if (
            next.size === state.mutedChatIds.length &&
            state.mutedChatIds.every((id) => next.has(id))
          ) {
            return state;
          }
          return { mutedChatIds: [...next] };
        });
      },
      toggleChatMute: (chatId) => {
        set((state) => ({
          mutedChatIds: state.mutedChatIds.includes(chatId)
            ? state.mutedChatIds.filter((id) => id !== chatId)
            : [...state.mutedChatIds, chatId],
        }));
      },
      deletedTails: {},
      noteDeletedTail: (chatId, text) => {
        set((state) => {
          const entries = Object.entries({ ...state.deletedTails, [chatId]: text });
          return { deletedTails: Object.fromEntries(entries.slice(-MAX_DELETED_TAILS)) };
        });
      },
      isChatMuted: (chatId) => (chatId == null ? false : get().mutedChatIds.includes(chatId)),
    }),
    {
      name: 'chat-settings',
      storage: createJSONStorage(() => localStorage),
    }
  )
);
